import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bell, Heart, MessageCircle, UserPlus, Calendar, 
  ShoppingBag, Star, ChevronRight, Loader2, RefreshCw 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for notification types
const getNotificationIcon = (type) => {
  const icons = {
    like: { icon: Heart, color: 'text-rose-500 bg-rose-50' },
    comment: { icon: MessageCircle, color: 'text-blue-500 bg-blue-50' },
    follow: { icon: UserPlus, color: 'text-purple-500 bg-purple-50' },
    booking: { icon: Calendar, color: 'text-orange-500 bg-orange-50' },
    order: { icon: ShoppingBag, color: 'text-emerald-500 bg-emerald-50' },
    astro: { icon: Star, color: 'text-amber-500 bg-amber-50' },
    system: { icon: Bell, color: 'text-slate-500 bg-slate-50' },
  };
  return icons[type] || icons.system;
};

// Format time ago
const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
  return date.toLocaleDateString();
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      // If no notifications exist yet, show empty state instead of error
      if (err.response?.status !== 404) {
        toast.error('Failed to load notifications');
      }
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: 'Unread' },
    { id: 'like', label: 'Likes' },
    { id: 'comment', label: 'Comments' },
    { id: 'follow', label: 'Follows' }
  ];

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read;
    return n.type === filter;
  });

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        const token = localStorage.getItem('dharmbook_token');
        await axios.put(`${API}/notifications/${notification.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
      } catch (err) {
        console.error('Error marking notification as read');
      }
    }

    // Navigate based on type
    if (notification.reference_type === 'post' && notification.reference_id) {
      navigate(`/post/${notification.reference_id}`);
    } else if (notification.reference_type === 'profile' && notification.reference_id) {
      navigate(`/profile/${notification.reference_id}`);
    } else if (notification.reference_type === 'order' && notification.reference_id) {
      navigate(`/shop/orders/${notification.reference_id}`);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-stone-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-14 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Notifications
              {unreadCount > 0 && (
                <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </h1>
            <div className="flex items-center gap-2">
              <button 
                onClick={fetchNotifications}
                className="p-2 text-slate-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              {unreadCount > 0 && (
                <button 
                  onClick={markAllRead}
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {filters.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${
                  filter === f.id
                    ? 'bg-orange-500 text-white'
                    : 'bg-stone-100 text-slate-600 hover:bg-stone-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-w-2xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-16 px-4">
            <Bell className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium mb-2">No notifications</p>
            <p className="text-sm text-slate-500">
              {filter === 'unread' 
                ? "You're all caught up!" 
                : "You'll be notified about activity here"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {filteredNotifications.map(notification => {
              const { icon: Icon, color } = getNotificationIcon(notification.type);
              return (
                <div 
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`flex items-start gap-3 p-4 hover:bg-stone-100 transition-colors cursor-pointer ${
                    !notification.read ? 'bg-orange-50/50' : 'bg-white'
                  }`}
                >
                  {/* Icon or Avatar */}
                  {notification.from_user_avatar ? (
                    <img 
                      src={notification.from_user_avatar}
                      alt={notification.from_user_name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`text-sm ${!notification.read ? 'font-semibold text-slate-800' : 'font-medium text-slate-700'}`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-slate-500 line-clamp-2 mt-0.5">
                          {notification.message}
                        </p>
                      </div>
                      {!notification.read && (
                        <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0 mt-2" />
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatTimeAgo(notification.created_at)}
                    </p>
                  </div>

                  {/* Arrow */}
                  <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
